import {
  Control,
  Controller,
  FieldErrors,
  get,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';

import { useEffect, useState } from 'react';
import { PolicyForm } from '../policy-dialog';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircle from '@mui/icons-material/AddCircle';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface PolicyRelationsAccordionProps {
  control: Control<PolicyForm>;
  errors: FieldErrors<PolicyForm>;
  watch: UseFormWatch<PolicyForm>;
  disabled: boolean;
  fieldName: `actionData.${number}.userModelTemplate.relations`;
}

export default function PolicyRelationsAccordion({
  control,
  errors,
  disabled,
  watch,
  fieldName,
}: PolicyRelationsAccordionProps) {
  const [open, setOpen] = useState(false);
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: fieldName,
  });

  const menuItems = [
    // { value: 'admin_assistant', label: 'Admin Assistant' },
    { value: 'assistant', label: 'Assistant' },
    { value: 'brother', label: 'Brother' },
    { value: 'child', label: 'Child' },
    { value: 'custom', label: 'Custom' },
    { value: 'domestic_partner', label: 'Domestic Partner' },
    // { value: 'dotted_line_manager', label: 'Dotted Line Manager' },
    // { value: 'exec_assistant', label: 'Executive Assistant' },
    { value: 'father', label: 'Father' },
    { value: 'friend', label: 'Friend' },
    { value: 'manager', label: 'Manager' },
    { value: 'mother', label: 'Mother' },
    { value: 'parent', label: 'Parent' },
    { value: 'partner', label: 'Partner' },
    { value: 'referred_by', label: 'Referred By' },
    { value: 'relative', label: 'Relative' },
    { value: 'sister', label: 'Sister' },
    { value: 'spouse', label: 'Spouse' },
  ];

  const relationsErrors = get(errors, fieldName);

  // close accordion if there are no items in the accordion
  useEffect(() => {
    if (fields.length === 0) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [fields]);

  // open accordion if there is an error in the accordion
  useEffect(() => {
    if (relationsErrors) {
      setOpen(true);
    }
  }, [relationsErrors]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        if (fields.length === 0) return;
        setOpen(!open);
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton
            disabled={fields.length === 0}
            sx={{ mr: -1 }}
            disableRipple
          >
            <ExpandMore />
          </IconButton>
        }
      >
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Typography
            sx={{
              color: (theme) =>
                relationsErrors ? theme.palette.error.main : 'inherit',
            }}
          >
            Relations
          </Typography>
          <Tooltip title="Add" arrow>
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  append({ type: '', customType: '', value: '' });
                }}
              >
                <AddCircle />
              </IconButton>
            </span>
          </Tooltip>
          {fields.length > 0 ? (
            <Chip label={fields.length} color="primary" size="small" />
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2 }}>
        {fields.map((item, index) => {
          const typeField = watch(`${fieldName}.${index}.type`);

          return (
            <Box key={item.id}>
              <Stack
                gap={1}
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ my: 1.5 }}
              >
                <Stack flexDirection="row" alignItems="center" gap={2}>
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`${fieldName}.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `relations-type-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `relations-type-${index}`,
                        }}
                        name="relations"
                        select
                        disabled={disabled}
                        label="Type"
                        error={!!relationsErrors?.[index]?.type}
                        helperText={
                          relationsErrors?.[index]?.type
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 150,
                        }}
                      >
                        {menuItems.map((item) => (
                          <MenuItem value={item.value} key={item.label}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  {typeField === 'custom' && (
                    <Controller
                      rules={{ required: 'This field is required.' }}
                      name={`${fieldName}.${index}.customType`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          name="relations"
                          inputRef={field.ref}
                          inputProps={{
                            id: `relations-customType-${index}`,
                          }}
                          InputLabelProps={{
                            htmlFor: `relations-customType-${index}`,
                          }}
                          disabled={disabled}
                          label="Custom Type"
                          error={!!relationsErrors?.[index]?.customType}
                          helperText={
                            relationsErrors?.[index]?.customType
                              ? 'This field is required.'
                              : ''
                          }
                          sx={{
                            minWidth: 225,
                            textTransform: 'capitalize',
                          }}
                        />
                      )}
                    />
                  )}
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`${fieldName}.${index}.value`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        name="relations"
                        inputRef={field.ref}
                        error={!!relationsErrors?.[index]?.value}
                        helperText={
                          relationsErrors?.[index]?.value
                            ? 'This field is required.'
                            : ''
                        }
                        disabled={disabled}
                        autoComplete="off"
                        label="Value"
                        sx={{
                          minWidth: 225,
                        }}
                      />
                    )}
                  />
                </Stack>

                {/* actions */}
                <Stack flexDirection="row" gap={2}>
                  <Tooltip title="Move Down" arrow>
                    <span>
                      <IconButton
                        disabled={index === fields.length - 1 || disabled}
                        size="small"
                        onClick={() => {
                          move(index, index + 1);
                        }}
                      >
                        <ArrowDownward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move Up" arrow>
                    <span>
                      <IconButton
                        disabled={index === 0 || disabled}
                        size="small"
                        onClick={() => {
                          move(index, index - 1);
                        }}
                      >
                        <ArrowUpward />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={disabled}
                        onClick={() => {
                          remove(index);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              {index !== fields.length - 1 && <Divider />}
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}
